import LastFourWeeksStorePerformanceCard from './last-four-weeks-store-performance-card'
import { useContext } from 'react'
import { HomeContext } from '../../state/home-context'
import { useApiGetLastFourWeeksStorePerformance } from 'core/api/home/useApiGetLastFourWeeksStorePerformance'
import { VpSkeleton } from '@vtmn-play/react'
import { computeDelta } from './last-four-weeks-store-performance-utils'
import LastFourWeeksStorePerformanceHeader from './last-four-weeks-store-performance-header'
import { FilterKey } from 'core/redux/reducers/filtersSlice'
import { getPathFromRouteId } from 'core/utils/route-utils'
import { RouteId } from 'core/routing/router/route-ids'
import { useNavigate } from 'react-router'
import WeeklyGmvSection from '../weekly-gmv-chart/weekly-gmv-section'
import { useFormatNumber } from 'core/hooks/useFormatNumber'
import { useFormatAmount } from 'core/hooks/useFormatAmount'
import { HOMEPAGE_CARD_REDIRECTS, HOMEPAGE_EVENTS } from 'core/constants/homepage-analytics'
import { useHomepageAnalytics } from '../../analytics/use-homepage-analytics'

const LastFourWeeksStorePerformance = () => {
  const navigate = useNavigate()
  const trackHomepageEvent = useHomepageAnalytics()
  const { storeId, aggregation, channel, store } = useContext(HomeContext)
  const currency = store?.currency ?? ''
  const shouldDisplay = !!storeId && aggregation === 'STORE'
  const formatNumber = useFormatNumber()
  const formatAmount = useFormatAmount()

  const { data, isLoading } = useApiGetLastFourWeeksStorePerformance({ storeId, channel }, shouldDisplay)

  const conversionDelta = data?.conversionRate ? computeDelta(data.conversionRate, formatNumber) : null
  const trafficDelta = data?.storeTraffic ? computeDelta(data.storeTraffic, formatNumber) : null
  const basketAverageValueDelta = data?.basketAverageValue ? computeDelta(data.basketAverageValue, formatNumber) : null
  const basketAverageQuantityDelta = data?.basketAverageQuantity
    ? computeDelta(data.basketAverageQuantity, formatNumber)
    : null

  const openManagerByMonth = () => {
    trackHomepageEvent(HOMEPAGE_EVENTS.CARD_REDIRECT_CLICK, { card: HOMEPAGE_CARD_REDIRECTS.LAST_FOUR_WEEKS_STORE })
    const search = new URLSearchParams({
      [FilterKey.STORE_ID]: String(storeId)
    })

    void navigate(`${getPathFromRouteId(RouteId.ManagerByMonth)}?${search}`)
  }

  return (
    <section className="mg-last-four-weeks-store-performance">
      {isLoading && (
        <div className="mg-last-four-weeks-store-performance-loading">
          <VpSkeleton />
        </div>
      )}

      {!isLoading && (
        <>
          <LastFourWeeksStorePerformanceHeader from={data?.from} to={data?.to} onOpenDetails={openManagerByMonth} />

          <div className="mg-last-four-weeks-store-performance-cards">
            <div className="mg-last-four-weeks-store-performance-cards-responsive-line">
              <LastFourWeeksStorePerformanceCard
                icon="price-tag"
                iconBackground="blue"
                titleKey="homepage.last-four-weeks-store-performance.card.average-ordered-value"
                value={
                  data?.basketAverageValue?.current
                    ? formatAmount(data.basketAverageValue.current, currency, '--', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })
                    : ''
                }
                delta={basketAverageValueDelta?.formatted ?? ''}
                deltaVariant={basketAverageValueDelta?.variant ?? 'positive'}
              />
              <LastFourWeeksStorePerformanceCard
                icon="t-shirt"
                iconBackground="purple"
                titleKey="homepage.last-four-weeks-store-performance.card.average-ordered-quantity"
                value={
                  data?.basketAverageQuantity?.current &&
                  formatNumber(data.basketAverageQuantity.current, '', {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1
                  })
                }
                delta={basketAverageQuantityDelta?.formatted ?? ''}
                deltaVariant={basketAverageQuantityDelta?.variant ?? 'positive'}
              />
            </div>
            <div className="mg-last-four-weeks-store-performance-cards-responsive-line">
              <LastFourWeeksStorePerformanceCard
                icon="shopping-bag"
                iconBackground="yellow"
                titleKey="homepage.last-four-weeks-store-performance.card.conversion-rate"
                value={
                  data?.conversionRate?.current &&
                  formatNumber(data.conversionRate.current / 100, '', {
                    style: 'percent',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })
                }
                delta={conversionDelta?.formatted ?? ''}
                deltaVariant={conversionDelta?.variant ?? 'positive'}
              />
              <LastFourWeeksStorePerformanceCard
                icon="group"
                iconBackground="sky"
                titleKey="homepage.last-four-weeks-store-performance.card.store-traffic"
                value={
                  data?.storeTraffic?.current &&
                  formatNumber(data.storeTraffic.current, '', {
                    maximumFractionDigits: 0
                  })
                }
                delta={trafficDelta?.formatted ?? ''}
                deltaVariant={trafficDelta?.variant ?? 'positive'}
              />
            </div>
          </div>

          <WeeklyGmvSection weeklyGmv={data?.weeklyGmv} currency={currency} />
        </>
      )}
    </section>
  )
}

export default LastFourWeeksStorePerformance
